import { useState, useMemo } from "react";
import {
  Search, PauseCircle, PlayCircle, Trash2, Loader2,
  Users, Wrench, UserX, UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminStatusBadge } from "@/admin/components/AdminStatusBadge";
import { ReasonModal } from "@/admin/components/ReasonModal";
import { useAdminUsers } from "@/admin/hooks/useAdminUsers";
import { useAdminAuth } from "@/admin/hooks/useAdminAuth";
import type { AdminUserRow, UserStatus, VerificationStatus } from "@/admin/types/admin";
import { VERIFICATION_STATUS_LABELS } from "@/admin/types/admin";

// ─── helpers ──────────────────────────────────────────────────────────────────

type TabKey = "all" | "clients" | "artisans";
type StatusFilter = "all" | UserStatus;

function isArtisan(role: AdminUserRow["role"]) {
  return role === "artisan_entreprise" || role === "artisan_bricoleur" || role === "artisan";
}

function RoleBadge({ role }: { role: AdminUserRow["role"] }) {
  if (isArtisan(role)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-orange-200">
        <Wrench className="h-3 w-3" />
        {role === "artisan_entreprise" ? "Entreprise" : "Bricoleur"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
      <UserCheck className="h-3 w-3" />
      Client
    </span>
  );
}

function VerifBadge({ status }: { status: VerificationStatus | null }) {
  if (!status || status === "notRequested") return <span className="text-gray-400 text-xs">—</span>;
  const colors: Record<VerificationStatus, string> = {
    notRequested: "text-gray-400",
    pending: "text-blue-600 bg-blue-50 ring-blue-200",
    verified: "text-green-700 bg-green-50 ring-green-200",
    rejected: "text-red-700 bg-red-50 ring-red-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${colors[status]}`}>
      {VERIFICATION_STATUS_LABELS[status]}
    </span>
  );
}

function Avatar({ name, url }: { name: string; url: string | null }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  if (url) {
    return <img src={url} alt={name} className="h-8 w-8 rounded-full object-cover flex-shrink-0" />;
  }
  return (
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
      {initials || "?"}
    </div>
  );
}

// ─── composant principal ──────────────────────────────────────────────────────

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [suspendTarget, setSuspendTarget] = useState<AdminUserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);

  const { query, suspendMutation, reactivateMutation, deleteMutation } = useAdminUsers(search);
  const { can, adminRole } = useAdminAuth();
  const isOwner = adminRole === "adminOwner";

  const allUsers = (query.data ?? []).filter((u) => u.role !== "admin");

  // Stats
  const stats = useMemo(() => ({
    clients: allUsers.filter((u) => !isArtisan(u.role)).length,
    artisans: allUsers.filter((u) => isArtisan(u.role)).length,
    suspended: allUsers.filter((u) => u.status === "suspended").length,
    deleted: allUsers.filter((u) => u.status === "deleted").length,
  }), [allUsers]);

  // Filtrage
  const filtered = useMemo(() => {
    return allUsers.filter((u) => {
      if (tab === "clients" && isArtisan(u.role)) return false;
      if (tab === "artisans" && !isArtisan(u.role)) return false;
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      return true;
    });
  }, [allUsers, tab, statusFilter]);

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "all", label: "Tous", count: allUsers.length },
    { key: "clients", label: "Clients", count: stats.clients },
    { key: "artisans", label: "Artisans", count: stats.artisans },
  ];

  const statusFilters: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "Tous les statuts" },
    { key: "active", label: "Actifs" },
    { key: "suspended", label: "Suspendus" },
    { key: "deleted", label: "Supprimés" },
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gestion des comptes clients et artisans</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { icon: UserCheck, label: "Clients", value: stats.clients, color: "bg-blue-500" },
          { icon: Wrench, label: "Artisans", value: stats.artisans, color: "bg-orange-500" },
          { icon: PauseCircle, label: "Suspendus", value: stats.suspended, color: "bg-yellow-500" },
          { icon: UserX, label: "Supprimés", value: stats.deleted, color: "bg-gray-400" },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${color}`}>
              <Icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Tabs */}
        <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 gap-0.5 w-fit">
          {tabs.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                tab === key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                tab === key ? "bg-primary/10 text-primary" : "bg-gray-200 text-gray-500"
              }`}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Recherche + filtre statut */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="pl-8 h-8 w-48 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {statusFilters.map(({ key, label }) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Résultat */}
      <p className="text-xs text-gray-400">{filtered.length} résultat(s)</p>

      {/* Table */}
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <Users className="h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-400">Aucun utilisateur ne correspond aux filtres</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Type</th>
                {tab !== "clients" && (
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Spécialité</th>
                )}
                {tab !== "clients" && (
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Vérification</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Wilaya</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Inscription</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                  {/* Utilisateur */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={user.fullName} url={user.avatarUrl} />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.fullName}</p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  {/* Type */}
                  <td className="px-4 py-3">
                    <RoleBadge role={user.role} />
                  </td>
                  {/* Spécialité (artisans seulement) */}
                  {tab !== "clients" && (
                    <td className="px-4 py-3 text-gray-600">
                      {isArtisan(user.role) ? (user.specialite || <span className="text-gray-300">—</span>) : <span className="text-gray-200">—</span>}
                    </td>
                  )}
                  {/* Vérification (artisans seulement) */}
                  {tab !== "clients" && (
                    <td className="px-4 py-3">
                      {isArtisan(user.role) ? <VerifBadge status={user.verificationStatus} /> : <span className="text-gray-200">—</span>}
                    </td>
                  )}
                  {/* Wilaya */}
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {user.wilaya || <span className="text-gray-300">—</span>}
                  </td>
                  {/* Statut */}
                  <td className="px-4 py-3">
                    <AdminStatusBadge status={user.status} />
                    {user.status === "suspended" && user.suspensionReason && (
                      <p className="mt-0.5 text-[10px] text-gray-400 max-w-[120px] truncate">{user.suspensionReason}</p>
                    )}
                  </td>
                  {/* Inscription */}
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(user.createdAt).toLocaleDateString("fr-FR")}
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {user.status === "active" && can("suspend_user") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 px-2 text-yellow-700 hover:bg-yellow-50 hover:text-yellow-800"
                          onClick={() => setSuspendTarget(user)}
                        >
                          <PauseCircle className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Suspendre</span>
                        </Button>
                      )}
                      {user.status === "suspended" && can("reactivate_user") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 px-2 text-green-700 hover:bg-green-50 hover:text-green-800"
                          disabled={reactivateMutation.isPending}
                          onClick={() => reactivateMutation.mutate(user.id)}
                        >
                          <PlayCircle className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Réactiver</span>
                        </Button>
                      )}
                      {isOwner && user.status !== "deleted" && can("delete_user") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setDeleteTarget(user)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Supprimer</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <ReasonModal
        open={!!suspendTarget}
        title={`Suspendre ${suspendTarget?.fullName ?? ""}`}
        reasonCategory="user_suspension"
        loading={suspendMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!suspendTarget) return;
          suspendMutation.mutate(
            { profileId: suspendTarget.id, userId: suspendTarget.userId, reasonCode, notes },
            { onSuccess: () => setSuspendTarget(null) }
          );
        }}
        onCancel={() => setSuspendTarget(null)}
      />
      <ReasonModal
        open={!!deleteTarget}
        title={`Supprimer le compte de ${deleteTarget?.fullName ?? ""}`}
        reasonCategory="user_deletion"
        loading={deleteMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!deleteTarget) return;
          deleteMutation.mutate(
            { profileId: deleteTarget.id, reasonCode, notes },
            { onSuccess: () => setDeleteTarget(null) }
          );
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
